import React from 'react';

const About: React.FC = () => {
  return (
    <div className="py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            About AgriMarket
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            We're revolutionizing agriculture in Kenya by connecting farmers directly with buyers, 
            eliminating middlemen and ensuring fair prices for everyone.
          </p>
        </div>

        {/* Mission & Vision */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          <div className="bg-gradient-to-br from-green-50 to-green-100 p-8 rounded-2xl">
            <div className="w-16 h-16 bg-gradient-to-r from-green-600 to-green-800 rounded-2xl flex items-center justify-center mb-6">
              <span className="text-white text-2xl">🎯</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h2>
            <p className="text-gray-600 leading-relaxed">
              To empower farmers across Kenya by providing them with direct access to markets, 
              fair pricing, and the tools they need to grow their businesses sustainably.
            </p>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-8 rounded-2xl">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl flex items-center justify-center mb-6">
              <span className="text-white text-2xl">👁️</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h2>
            <p className="text-gray-600 leading-relaxed">
              To create a thriving agricultural ecosystem where farmers and consumers benefit 
              from transparent, efficient, and sustainable food supply chains.
            </p>
          </div>
        </div>

        {/* Story */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">Our Story</h2>
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-lg mx-auto text-gray-600">
              <p className="mb-6">
                AgriMarket was born from a simple observation: farmers in Kenya were struggling to get 
                fair prices for their produce, while consumers were paying high prices for food that 
                had passed through multiple middlemen.
              </p>
              <p className="mb-6">
                Founded in 2024 by a team of agricultural experts and technology enthusiasts, 
                we set out to bridge this gap using modern technology and innovative solutions. 
                Our platform connects farmers directly with buyers, ensuring transparency, 
                fair pricing, and quality assurance.
              </p>
              <p className="mb-6">
                Today, we're proud to serve thousands of farmers and buyers across Kenya, 
                helping to build a more sustainable and equitable agricultural sector.
              </p>
            </div>
          </div>
        </div>

        {/* Values */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Our Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-r from-green-600 to-green-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-2xl">🤝</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Transparency</h3>
              <p className="text-gray-600">Open pricing and clear communication between all parties</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-r from-blue-600 to-blue-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-2xl">⚖️</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Fairness</h3>
              <p className="text-gray-600">Ensuring fair prices and treatment for farmers and buyers</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-r from-yellow-600 to-yellow-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-2xl">🌱</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Sustainability</h3>
              <p className="text-gray-600">Promoting sustainable farming practices and environmental care</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-r from-purple-600 to-purple-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-2xl">🚀</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Innovation</h3>
              <p className="text-gray-600">Using technology to solve agricultural challenges</p>
            </div>
          </div>
        </div>

        {/* Team */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Meet Our Team</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl shadow-md p-6 text-center">
              <div className="w-20 h-20 bg-gradient-to-r from-green-600 to-green-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-2xl">👨‍💼</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">John Mwangi</h3>
              <p className="text-green-600 font-medium mb-2">CEO & Co-Founder</p>
              <p className="text-gray-600 text-sm">Agricultural economist with 15+ years experience in rural development</p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6 text-center">
              <div className="w-20 h-20 bg-gradient-to-r from-blue-600 to-blue-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-2xl">👩‍💻</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Grace Akinyi</h3>
              <p className="text-green-600 font-medium mb-2">CTO & Co-Founder</p>
              <p className="text-gray-600 text-sm">Technology expert passionate about solving real-world problems</p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6 text-center">
              <div className="w-20 h-20 bg-gradient-to-r from-yellow-600 to-yellow-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-2xl">👨‍🌾</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Peter Kimani</h3>
              <p className="text-green-600 font-medium mb-2">Head of Farmer Relations</p>
              <p className="text-gray-600 text-sm">Former farmer with deep understanding of agricultural challenges</p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="bg-gradient-to-r from-green-600 to-green-800 rounded-2xl p-12 text-white text-center">
          <h2 className="text-3xl font-bold mb-12">Our Impact</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="text-4xl font-bold mb-2">500+</div>
              <div className="text-green-100">Active Farmers</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">2,000+</div>
              <div className="text-green-100">Happy Customers</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">10,000+</div>
              <div className="text-green-100">Orders Delivered</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">47</div>
              <div className="text-green-100">Counties Served</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
